import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Globe, Facebook, Twitter, Instagram, Youtube, ChevronRight, Users, Clock } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default values (fallback if API fails)
const defaultQuickLinks = [
  { label: 'About Us', href: '/about' },
  { label: 'Research', href: '/research' },
  { label: 'Publications', href: '/publications' },
  { label: 'Careers', href: '/jobs' },
  { label: 'Tenders', href: '/tenders' },
  { label: 'VKSA', href: '/vksa' },
  { label: 'RTI', href: 'https://rtionline.gov.in/' }
];

const defaultServices = [
  { label: 'Library', href: '/library' },
  { label: 'Museum', href: '/museum' },
  { label: 'Nursery', href: '/nursery' },
  { label: 'Consultancy', href: '/consultancy' },
  { label: 'Contract Research', href: '/contract-research' },
  { label: 'Education', href: '/education' },
  { label: 'Auditorium', href: '/auditorium' },
  { label: 'Guest House', href: '/guest-house' }
];

const socialIcons = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  youtube: Youtube
};

export default function Footer() {
  const { t } = useI18n();
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);
  const [visitorCount, setVisitorCount] = useState(0);
  const [lastUpdatedDate, setLastUpdatedDate] = useState('24 Sep 2026');

  useEffect(() => {
    // Fetch site settings
    fetch(`${BACKEND_URL}/api/site-settings`)
      .then(res => res.json())
      .then(data => {
        setSettings(data);
        if (data.last_updated) {
          setLastUpdatedDate(data.last_updated);
        } else if (data.updated_at) {
          try {
            const dt = new Date(data.updated_at);
            if (!isNaN(dt.getTime())) {
              setLastUpdatedDate(dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
            }
          } catch (e) {}
        }
        if (data.visitor_count && data.visitor_count > 0) {
          setVisitorCount(prev => prev > 0 ? prev : data.visitor_count);
        }
      })
      .catch(console.error);

    // Get visitor count (session-based - only increments once per session)
    fetch(`${BACKEND_URL}/api/visitor-count/increment`, {
      method: 'POST',
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data && typeof data.count === 'number' && data.count > 0) {
          setVisitorCount(data.count);
        }
      })
      .catch(() => {
        // Fallback to GET visitor-count if increment POST fails
        fetch(`${BACKEND_URL}/api/visitor-count`)
          .then(res => res.json())
          .then(data => {
            if (data && data.count) setVisitorCount(data.count);
          })
          .catch(console.error);
      });
  }, []);

  const footer = settings?.footer || {};
  const section1 = footer.section1 || {};
  // Use hardcoded defaults for links to ensure consistency as per user request
  // This overrides potentially incorrect links from the backend API
  const section2 = { ...footer.section2, links: defaultQuickLinks };
  const section3 = { ...footer.section3, links: defaultServices };
  const section4 = footer.section4 || {};
  const socialLinks = section1.social_links || [];

  return (
    <footer className="relative" data-testid="footer">
      {/* Main footer */}
      <div className="bg-[#0C4C20] relative overflow-hidden">
        {/* Decorative pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cpath d='M50 20c-16.569 0-30 13.431-30 30 0 16.569 13.431 30 30 30 16.569 0 30-13.431 30-30 0-16.569-13.431-30-30-30zm0 5c13.807 0 25 11.193 25 25s-11.193 25-25 25-25-11.193-25-25 11.193-25 25-25z' fill='%23ffffff' fill-opacity='0.1'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }} />

        <div className="container-cish py-8 md:py-12 relative z-10">
          {/* Mobile: 2 columns, Tablet: 2 columns, Desktop: 4 columns */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {/* Institute info (Section 1) */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-white">
                {section1.title || 'ICAR-CISH'}
              </h3>
              <p className="text-xs md:text-sm text-[#B8D4B8] leading-relaxed mb-3 md:mb-4">
                {section1.description || 'The Central Institute for Subtropical Horticulture (CISH) was started as Central Mango Research Station on September 4, 1972.'}
              </p>
              {/* Social icons */}
              <div className="flex gap-2 mt-3 md:mt-4">
                {socialLinks.map((social, index) => {
                  const IconComponent = socialIcons[social.platform] || Globe;
                  return (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded bg-[#0E6D2B] flex items-center justify-center hover:bg-[#21A366] transition-colors"
                      aria-label={social.platform}
                      data-testid={`footer-${social.platform}`}
                    >
                      <IconComponent className="w-4 h-4 text-white" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Quick Links (Section 2) */}
            <div>
              <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-white">
                {section2.title || 'Quick Links'}
              </h3>
              <ul className="space-y-1.5 md:space-y-2">
                {(section2.links || defaultQuickLinks).map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      target={link.href.startsWith('http') ? "_blank" : undefined}
                      rel={link.href.startsWith('http') ? "noopener noreferrer" : undefined}
                      className="text-xs md:text-sm text-[#B8D4B8] hover:text-white flex items-center gap-1 transition-colors"
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <ChevronRight className="w-3 h-3 text-[#21A366] flex-shrink-0" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services (Section 3) */}
            <div>
              <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-white">
                {section3.title || 'Services'}
              </h3>
              <ul className="space-y-1.5 md:space-y-2">
                {(section3.links || defaultServices).map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      target={link.href.startsWith('http') ? "_blank" : undefined}
                      rel={link.href.startsWith('http') ? "noopener noreferrer" : undefined}
                      className="text-xs md:text-sm text-[#B8D4B8] hover:text-white flex items-center gap-1 transition-colors"
                      data-testid={`footer-service-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <ChevronRight className="w-3 h-3 text-[#21A366] flex-shrink-0" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Information (Section 4) */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-white">
                {section4.title || 'Contact Information'}
              </h3>
              <ul className="space-y-2 md:space-y-3">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#21A366]" />
                  <span className="text-xs md:text-sm text-[#B8D4B8]">
                    ICAR-Central Institute for Subtropical Horticulture, Rehmankhera, P.O. Kakori, Lucknow - 226101, Uttar Pradesh, India
                  </span>
                </li>
                {section4.fax && (
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4 shrink-0 text-[#21A366]" />
                    <span className="text-xs md:text-sm text-[#B8D4B8]">Fax: {section4.fax}</span>
                  </li>
                )}
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 shrink-0 text-[#21A366]" />
                  <a
                    href="tel:+916306965633"
                    className="text-xs md:text-sm text-[#B8D4B8] hover:text-white"
                  >
                    +91-6306965633
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 shrink-0 text-[#21A366]" />
                  <a
                    href="mailto:cish.lucknow@gmail.com"
                    className="text-xs md:text-sm text-[#B8D4B8] hover:text-white"
                  >
                   cish.lucknow@gmail.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="w-4 h-4 shrink-0 text-[#21A366]" />
                  <a
                    href="https://www.cish.org.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs md:text-sm text-[#B8D4B8] hover:text-white"
                  >
                    www.cish.org.in
                  </a>
                </li>
              </ul>
      </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-[#054018] border-t border-[#0E6D2B]">
        <div className="container-cish py-3 md:py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 md:gap-3">
            <p className="text-[10px] md:text-xs text-[#B8D4B8] text-center md:text-left">
              © {currentYear} Central Institute For Subtropical Horticulture. All Rights Reserved.
            </p>
            <div className="flex items-center gap-3 md:gap-4 text-[10px] md:text-xs">
              {/* Visitor Counter */}
              <span className="flex items-center gap-1 text-[#21A366]" data-testid="visitor-counter">
                <Users className="w-3 h-3" />
                <span className="text-[#B8D4B8]">Total Visitors: <span className="font-bold text-white">{visitorCount.toLocaleString()}</span></span>
              </span>
              <span className="text-[#0E6D2B]">|</span>
              <span className="flex items-center gap-1 text-[#21A366]">
                <Clock className="w-3 h-3" />
                <span className="text-[#B8D4B8]">Last Updated: <span className="font-bold text-white">{lastUpdatedDate}</span></span>
              </span>
              <span className="text-[#0E6D2B]">|</span>
              <a href="/privacy-policy" className="text-[#B8D4B8] hover:text-white transition-colors">
                Privacy Policy
              </a>
              <span className="text-[#0E6D2B]">|</span>
              <a href="/terms-of-use" className="text-[#B8D4B8] hover:text-white transition-colors">
                Terms of Use
              </a>
              <span className="text-[#0E6D2B]">|</span>
              <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer" className="text-[#B8D4B8] hover:text-white transition-colors">
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
